import React, { useCallback, useEffect, useImperativeHandle } from 'react';
import { useVideoPlayer, VideoView, VideoPlayer } from 'expo-video';
import * as ScreenOrientation from 'expo-screen-orientation';
import { Platform } from 'react-native';
import { useVideoPlayerStore } from '@/student/task/components/VideoPlayer/store/videoPlayerStore';
import log from '@/common/utils/logger';

interface VideoPlayerCoreProps {
  accessUrl: string;
  onError?: (error: string) => void;
  onReadyForDisplay?: (player: VideoPlayer) => void;
  autoPlay?: boolean;
  pauseOnReady?: boolean;
}

/**
 * Returns true for AbortErrors from play() that are expected browser behavior.
 *
 * expo-video on Web wraps the DOMException as a plain Error with message starting
 * "AbortError: ...", so we check both .name and .message to catch both shapes.
 * See FLARE-29: play() interrupted by new load request should not reach Sentry.
 */
const isAbortError = (error: unknown): boolean => {
  const typedError = error as { name?: string; message?: string } | undefined;
  return (
    typedError?.name === 'AbortError'
    || (typedError?.message?.includes('AbortError') ?? false)
  );
};

/**
 * Core player wiring to expo-video. Handles state updates and optional autoplay.
 */
export const VideoPlayerCore = React.forwardRef<VideoPlayer, VideoPlayerCoreProps>(
  ({
    accessUrl,
    onError,
    onReadyForDisplay,
    autoPlay = true,
    pauseOnReady = false,
  }, ref) => {
    const setPlaybackStatus = useVideoPlayerStore((state) => state.setPlaybackStatus);
    const setPlaybackPosition = useVideoPlayerStore((state) => state.setPlaybackPosition);
    const setFullscreenStatus = useVideoPlayerStore((state) => state.setFullscreenStatus);

    const onReadyCalledRef = React.useRef(false);
    const videoViewRef = React.useRef<React.ComponentRef<typeof VideoView>>(null);

    const player = useVideoPlayer(accessUrl, (p) => {
      const playerInstance = p;
      playerInstance.loop = false;
      // Enable time updates for transcript synchronization (update every 250ms)
      playerInstance.timeUpdateEventInterval = 0.25;
      if (pauseOnReady) {
        playerInstance.pause();
      }
      // Don't play immediately - wait for readyToPlay status
    });

    // Forward the video player object
    useImperativeHandle(ref, () => player, [player]);

    // Reset ready flag when source changes
    useEffect(() => {
      onReadyCalledRef.current = false;
    }, [accessUrl]);

    const handlePlayError = useCallback((error: unknown) => {
      if (isAbortError(error)) {
        return;
      }
      log.error('Video playback error:', error);
      onError?.('Failed to play video');
    }, [onError]);

    const playWithHandling = useCallback((playerInstance: VideoPlayer) => {
      if (!playerInstance) return;

      try {
        const playResult = playerInstance.play() as Promise<void> | void;
        if (playResult && typeof playResult.catch === 'function') {
          playResult.catch((error) => {
            if (isAbortError(error)) {
              return;
            }
            handlePlayError(error);
          });
        }
      } catch (error) {
        handlePlayError(error);
      }
    }, [handlePlayError]);

    // Handle playback status and position updates separately to avoid listener churn
    useEffect(() => {
      const timeUpdateSubscription = player.addListener('timeUpdate', (event) => {
        // Current currentTime and duration are in seconds, convert to ms for store compatibility
        const { currentTime } = event;
        const duration = player.duration || 0;
        setPlaybackPosition(currentTime * 1000, duration * 1000);
      });

      return () => timeUpdateSubscription.remove();
    }, [player, setPlaybackPosition]);

    useEffect(() => {
      const playSubscription = player.addListener('playingChange', (event) => {
        setPlaybackStatus(event.isPlaying);
      });

      return () => playSubscription.remove();
    }, [player, setPlaybackStatus]);

    useEffect(() => {
      const statusSubscription = player.addListener('statusChange', (event) => {
        const { status, error } = event;
        if (status === 'error') {
          // AbortErrors are expected browser behavior (play() interrupted by source change).
          // Silencing here covers the case where expo-video fires a statusChange 'error' event
          // in addition to (or instead of) rejecting the play() Promise (FLARE-29).
          if (isAbortError(error)) {
            return;
          }
          // Only escalate to Sentry ERROR when the player provides a meaningful message.
          // Empty-message errors (e.g. URL expiry, codec mismatch with no detail) go
          // as breadcrumbs only to avoid Sentry quota noise.
          const errorMessage = (error as { message?: string } | undefined)?.message;
          if (errorMessage) {
            log.error('Video playback error:', error);
          } else {
            log.warn('Video playback status error (no message from player)');
          }
          onError?.('Failed to play video');
        } else if (status === 'readyToPlay') {
          if (!onReadyCalledRef.current) {
            onReadyCalledRef.current = true;
            // Handle initial state before notifying parent or starting playback
            if (pauseOnReady) {
              player.pause();
            }
            onReadyForDisplay?.(player);
            // Auto-play once everything is set up
            if (autoPlay) {
              playWithHandling(player);
            }
          }
        }
      });

      // Handle cases where the player might already be ready when listeners are attached
      if (player.status === 'readyToPlay' && !onReadyCalledRef.current) {
        onReadyCalledRef.current = true;
        if (pauseOnReady) {
          player.pause();
        }
        onReadyForDisplay?.(player);
        if (autoPlay) {
          playWithHandling(player);
        }
      }

      return () => statusSubscription.remove();
    }, [player, onError, onReadyForDisplay, autoPlay, pauseOnReady, playWithHandling]);

    // Handle fullscreen state changes
    const handleFullscreenEnter = useCallback(async () => {
      if (Platform.OS === 'web') return;
      try {
        await ScreenOrientation.unlockAsync();
        setFullscreenStatus(true);
      } catch (error) {
        log.error('Error handling orientation (enter):', error);
      }
    }, [setFullscreenStatus]);

    const handleFullscreenExit = useCallback(async () => {
      if (Platform.OS === 'web') return;
      try {
        // Only lock back to portrait if we are not in a landscape state that requires it unlocked
        // (Actually, usually we want to lock back to portrait when exiting fullscreen)
        await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
        setFullscreenStatus(false);
      } catch (error) {
        log.error('Error handling orientation (exit):', error);
      }
    }, [setFullscreenStatus]);

    // Auto-fullscreen on rotation to landscape (YouTube-like behavior)
    useEffect(() => {
      if (Platform.OS === 'web') return () => {};

      let subscription: ScreenOrientation.Subscription | null = null;

      const setupOrientationListener = async () => {
        try {
          // Unlock orientation while player is mounted to allow rotation
          await ScreenOrientation.unlockAsync();

          subscription = ScreenOrientation.addOrientationChangeListener(async (event) => {
            const { orientation } = event.orientationInfo;
            const isLandscape = orientation === ScreenOrientation.Orientation.LANDSCAPE_LEFT
              || orientation === ScreenOrientation.Orientation.LANDSCAPE_RIGHT;

            // Auto-enter fullscreen on landscape rotation (YouTube-like)
            if (isLandscape && videoViewRef.current) {
              try {
                await videoViewRef.current.enterFullscreen();
              } catch (error) {
                log.debug('Failed to enter fullscreen on rotation:', error);
              }
            }
          });
        } catch (error) {
          log.error('Failed to setup orientation listener:', error);
        }
      };

      setupOrientationListener();

      return () => {
        if (subscription) {
          ScreenOrientation.removeOrientationChangeListener(subscription);
        }
        // Lock back to portrait when player unmounts
        ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch((err) => {
          log.debug('Failed to lock orientation on unmount:', err);
        });
      };
    }, []);

    return (
      <VideoView
        ref={videoViewRef}
        player={player}
        nativeControls
        allowsPictureInPicture
        fullscreenOptions={{
          enable: true,
        }}
        // flex: 1 alone can cause overflow on web when native controls load
        // width/height: 100% ensures VideoView fills parent without exceeding it
        // eslint-disable-next-line react-native/no-inline-styles
        style={{ flex: 1, width: '100%', height: '100%' }}
        contentFit="contain"
        onFullscreenEnter={handleFullscreenEnter}
        onFullscreenExit={handleFullscreenExit}
      />
    );
  },
);
